import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useTranslations } from '../context/TranslationsContext';
import { 
  Clock, Package, Info, 
  Check, Ticket, ArrowRight 
} from 'lucide-react';
import { cn } from '../lib/utils';

const API = process.env.REACT_APP_BACKEND_URL;

// Schedule Card Component - Consistent glass-card style
const ScheduleCard = ({ schedule, lang, t, index }) => {
  const mainOptions = schedule.options?.filter(o => o.type !== 'lasthour') || [];
  const lastHourOption = schedule.options?.find(o => o.type === 'lasthour');
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      className="glass-card h-full flex flex-col"
      data-testid={`schedule-${schedule.schedule_id}`}
    >
      {/* Header */}
      <div className="p-6 pb-4 border-b border-white/10 dark:border-white/10">
        <h3 className="text-xl font-['Syne'] font-bold">
          {schedule.name?.[lang] || schedule.name?.pl}
        </h3>
      </div>

      {/* Content */}
      <ScheduleOptions options={mainOptions} lang={lang} t={t} />
      
      {/* Last hour - always at bottom */}
      {lastHourOption && (
        <div className="p-6 pt-0 mt-auto">
          <div className="flex items-center justify-between py-3 px-4 rounded-xl bg-black/5 dark:bg-white/5">
            <span className="text-sm text-muted-foreground">
              {lastHourOption.name?.[lang] || lastHourOption.name?.pl}
            </span>
            <span className="font-bold">
              {lastHourOption.price} zł
            </span>
          </div>
        </div>
      )}
    </motion.div>
  );
};

// Weekend Card with Sat/Sun tabs
const WeekendCard = ({ satSchedule, sunSchedule, lang, t, index }) => {
  const [activeDay, setActiveDay] = React.useState('sat');
  const schedule = activeDay === 'sat' ? satSchedule : sunSchedule;
  const mainOptions = schedule.options?.filter(o => o.type !== 'lasthour') || [];
  const lastHourOption = schedule.options?.find(o => o.type === 'lasthour');

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      className="glass-card h-full flex flex-col"
      data-testid="schedule-weekend"
    >
      {/* Header with day tabs - stacked on small screens */}
      <div className="p-6 pb-4 border-b border-white/10 dark:border-white/10">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <h3 className="text-xl font-['Syne'] font-bold shrink-0">Weekend</h3>
          <div className="flex p-1 rounded-xl bg-black/10 dark:bg-white/5 w-full sm:w-auto sm:ml-auto">
            <button
              onClick={() => setActiveDay('sat')}
              className={cn(
                "flex-1 sm:flex-none px-4 py-2 rounded-lg text-sm font-bold transition-all",
                activeDay === 'sat'
                  ? "bg-primary text-white shadow-md"
                  : "text-muted-foreground hover:text-foreground"
              )}
              data-testid="weekend-tab-sat"
            >
              {satSchedule.name?.[lang] || 'Sobota'}
            </button>
            <button
              onClick={() => setActiveDay('sun')}
              className={cn(
                "flex-1 sm:flex-none px-4 py-2 rounded-lg text-sm font-bold transition-all",
                activeDay === 'sun'
                  ? "bg-primary text-white shadow-md"
                  : "text-muted-foreground hover:text-foreground"
              )}
              data-testid="weekend-tab-sun"
            >
              {sunSchedule.name?.[lang] || 'Niedziela'}
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <ScheduleOptions options={mainOptions} lang={lang} t={t} />

      {/* Last hour */}
      {lastHourOption && (
        <div className="p-6 pt-0 mt-auto">
          <div className="flex items-center justify-between py-3 px-4 rounded-xl bg-black/5 dark:bg-white/5">
            <span className="text-sm text-muted-foreground">
              {lastHourOption.name?.[lang] || lastHourOption.name?.pl}
            </span>
            <span className="font-bold">
              {lastHourOption.price} zł
            </span>
          </div>
        </div>
      )}
    </motion.div>
  );
};

// Shared options renderer
const ScheduleOptions = ({ options, lang, t }) => (
  <div className="p-6 flex-1 space-y-4">
    {options.map((option) => (
      <div key={option.option_id} className="space-x-2">
        <div className="text-md text-muted-foreground mb-2">
          {option.name?.[lang] || option.name?.pl}
        </div>
        
        <div className="flex items-center gap-4 mb-3">
          {/* Walk-in price */}
          <div className="flex-1">
            <div className="text-xs text-muted-foreground mb-1">
              {t('pricing.walkIn', 'Bez rezerwacji')}
            </div>
            <div className="flex items-baseline gap-1">
              <span className="text-2xl font-bold">{option.price}</span>
              <span className="text-sm text-muted-foreground">zł</span>
            </div>
          </div>
          
          {/* Reservation price */}
          {option.priceWithReservation !== null && (
            <div className="flex-1 p-3 rounded-xl bg-primary/10 border border-primary/20">
              <div className="text-xs text-primary mb-1 flex items-center gap-1">
                <Check className="w-3 h-3" />
                {t('pricing.withBooking', 'Z rezerwacją')}
              </div>
              <div className="flex items-baseline gap-1">
                <span className="text-2xl font-bold text-primary">{option.priceWithReservation}</span>
                <span className="text-sm text-primary/70">zł</span>
              </div>
            </div>
          )}
        </div>

        {/* Session time slots */}
        {option.sessionSlots && option.sessionSlots.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-8">
            {option.sessionSlots.map((slot, si) => (
              <span 
                key={si}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-mono bg-primary/10 text-primary border border-primary/20"
              >
                <Clock className="w-3 h-3" />
                {slot}
              </span>
            ))}
          </div>
        )}
        {(!option.sessionSlots || option.sessionSlots.length === 0) && <div className="mb-8" />}
      </div>
    ))}
  </div>
);

// Rental Item Component - Simple list style
const RentalItem = ({ rental, lang, index }) => (
  <motion.div
    initial={{ opacity: 0, x: -10 }}
    whileInView={{ opacity: 1, x: 0 }}
    viewport={{ once: true }}
    transition={{ delay: index * 0.05 }}
    className="flex items-center justify-between py-4 border-b border-white/5 dark:border-white/5 last:border-0"
    data-testid={`rental-${rental.rental_id}`}
  >
    <span className="text-muted-foreground">
      {rental.name?.[lang] || rental.name?.pl}
    </span>
    <span className="text-xl font-bold">
      {rental.price} <span className="text-sm font-normal text-muted-foreground">zł</span>
    </span>
  </motion.div>
);

const PricingPage = () => {
  const { t: ti18n, i18n } = useTranslation();
  const { tr } = useTranslations();
  const lang = i18n.language;
  
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('sessions');
  
  // Smart translate: database first, fallback to i18n
  const t = (key, fallback) => {
    return tr(key, ti18n(key, fallback || key));
  };

  useEffect(() => {
    fetchPricing();
  }, []);

  const fetchPricing = async () => {
    try {
      const response = await axios.get(`${API}/api/pricing`);
      setPricing(response.data);
    } catch (error) {
      console.error('Failed to fetch pricing:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-primary/20 rounded-full animate-spin border-t-primary" />
        </div>
      </div>
    );
  }

  const schedules = pricing?.schedules || [];
  const rentals = pricing?.rentals || [];

  return (
    <div className="min-h-screen pt-32 pb-20 px-6" data-testid="pricing-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h1 className="text-5xl md:text-6xl font-['Syne'] font-bold mb-4">
            {tr('pricing.title', 'Cennik')}
          </h1>
          <p className="text-lg text-muted-foreground max-w-xl mx-auto">
            {tr('pricing.bookOnlineInfo', 'Zarezerwuj online i oszczędź!')}
          </p>
        </motion.div>

        {/* Tabs */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex justify-center mb-12"
        >
          <div className="inline-flex p-1 rounded-full bg-black/5 dark:bg-white/5">
            <button
              onClick={() => setActiveTab('sessions')}
              className={cn(
                "px-6 py-2.5 rounded-full font-medium transition-all flex items-center gap-2",
                activeTab === 'sessions' 
                  ? "bg-primary text-white" 
                  : "text-muted-foreground hover:text-foreground"
              )}
              data-testid="tab-sessions"
            >
              <Clock className="w-4 h-4" />
              {t('pricing.sessions', 'Wejścia')}
            </button>
            <button
              onClick={() => setActiveTab('rentals')}
              className={cn(
                "px-6 py-2.5 rounded-full font-medium transition-all flex items-center gap-2",
                activeTab === 'rentals' 
                  ? "bg-primary text-white" 
                  : "text-muted-foreground hover:text-foreground"
              )}
              data-testid="tab-rentals"
            >
              <Package className="w-4 h-4" />
              {t('pricing.rentals', 'Wypożyczalnia')}
            </button>
          </div>
        </motion.div>

        {/* Sessions Tab */}
        {activeTab === 'sessions' && (
          <div className="space-y-10">
            {/* Pricing Cards */}
            <div className="grid md:grid-cols-3 gap-6">
              {(() => {
                const satSchedule = schedules.find(s => s.schedule_id === 'sat');
                const sunSchedule = schedules.find(s => s.schedule_id === 'sun');
                const hasWeekendSplit = satSchedule && sunSchedule;
                const regularSchedules = hasWeekendSplit
                  ? schedules.filter(s => s.schedule_id !== 'sat' && s.schedule_id !== 'sun')
                  : schedules;

                return (
                  <>
                    {regularSchedules.map((schedule, index) => (
                      <ScheduleCard
                        key={schedule.schedule_id}
                        schedule={schedule}
                        lang={lang}
                        t={t}
                        index={index}
                      />
                    ))}
                    {hasWeekendSplit && (
                      <WeekendCard
                        satSchedule={satSchedule}
                        sunSchedule={sunSchedule}
                        lang={lang}
                        t={t}
                        index={regularSchedules.length}
                      />
                    )}
                  </>
                );
              })()}
            </div>

            {/* Book Now CTA */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <Link 
                to="/booking" 
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold bg-primary text-white hover:bg-primary/90 transition-colors"
                data-testid="book-now-btn"
              >
                <Ticket className="w-5 h-5" />
                {t('pricing.bookNow', 'Zarezerwuj teraz')}
                <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>
          </div>
        )}

        {/* Rentals Tab */}
        {activeTab === 'rentals' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="max-w-xl mx-auto"
          >
            <div className="glass-card overflow-hidden">
              {/* Header */}
              <div className="p-6 border-b border-white/10 dark:border-white/10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
                    <Package className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="text-xl font-['Syne'] font-bold">
                    {t('pricing.equipmentRental', 'Wypożyczalnia sprzętu')}
                  </h3>
                </div>
              </div>
              
              {/* Items */}
              <div className="p-6">
                {rentals.map((rental, index) => (
                  <RentalItem 
                    key={rental.rental_id} 
                    rental={rental} 
                    lang={lang}
                    index={index}
                  />
                ))}
              </div>

              {/* Info Note */}
              <div className="px-6 pb-6">
                <div className="flex items-start gap-3 p-4 rounded-xl bg-primary/5 border border-primary/10">
                  <Info className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                  <p className="text-muted-foreground text-sm">
                    {t('pricing.rentalInfoNote', 'Cena uwzględnia wypożyczenie sprzętu na czas pobytu.')}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* CTA Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-20"
        >
          <div className="glass-card p-8 md:p-12 text-center">
            <h2 className="text-3xl md:text-4xl font-['Syne'] font-bold mb-4">
              {t('pricing.haveQuestions', 'Masz pytania?')}
            </h2>
            <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
              {t('pricing.contactUsDescription', 'Skontaktuj się z nami - chętnie pomożemy dobrać najlepszą opcję dla Ciebie i Twojej rodziny.')}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link 
                to="/contact" 
                className="px-6 py-3 rounded-full border border-white/20 dark:border-white/20 font-medium hover:bg-white/5 transition-colors"
                data-testid="contact-btn"
              >
                {t('pricing.contact', 'Kontakt')}
              </Link>
              <Link 
                to="/faq" 
                className="px-6 py-3 rounded-full bg-primary text-white font-medium hover:bg-primary/90 transition-colors"
                data-testid="faq-btn"
              >
                {t('pricing.viewFaq', 'Zobacz FAQ')}
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PricingPage;
